<script lang="ts">
  /**
   * "New game": the one filled tile on Home, always first and always in the
   * same place, whatever is in progress below it (the owner's round-2 review:
   * starting something new must never look like continuing something).
   *
   * Filled, with a + and a chevron; every Continue on the same screen is a
   * light button inside its own game's card (`GameCard`), so the two cannot
   * be mistaken for each other.
   *
   * `primary` marks it as the screen's primary action (`data-shell-primary`),
   * which is where a game sends focus when whatever had it has gone — an
   * abandoned game's card, say.
   *
   * `icon` (since 1.2.0) replaces the plus, for a tile that starts something
   * without adding anything (Interlock's "Play").
   *
   * Test id: `testid`. No default.
   */
  import type { Snippet } from 'svelte';

  interface Props {
    readonly title: string;
    readonly detail?: string | undefined;
    readonly onclick: () => void;
    /** The glyph in the round well, 22px, in `currentColor`; a plus by default. */
    readonly icon?: Snippet | undefined;
    readonly primary?: boolean;
    readonly testid?: string | undefined;
  }
  const { title, detail, onclick, icon, primary = false, testid }: Props = $props();
</script>

<button
  type="button"
  class="ui-new-game"
  data-testid={testid}
  data-shell-primary={primary ? '' : undefined}
  {onclick}
>
  <span class="ui-new-game-plus" aria-hidden="true">
    {#if icon !== undefined}
      {@render icon()}
    {:else}
      <svg viewBox="0 0 24 24" width="22" height="22">
        <path
          d="M12 5v14M5 12h14"
          fill="none"
          stroke="currentColor"
          stroke-width="2.6"
          stroke-linecap="round"
        />
      </svg>
    {/if}
  </span>
  <span class="ui-new-game-text">
    <b>{title}</b>
    {#if detail !== undefined}
      <span>{detail}</span>
    {/if}
  </span>
  <svg class="ui-new-game-chevron" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path
      d="M9 5l7 7-7 7"
      fill="none"
      stroke="currentColor"
      stroke-width="2.4"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
</button>

<style>
  .ui-new-game {
    width: 100%;
    display: flex;
    align-items: center;
    gap: var(--ui-space-3);
    min-height: 4.5rem;
    padding: var(--ui-space-3) var(--ui-space-4);
    border: 0;
    border-radius: var(--ui-radius-lg);
    background: var(--ui-accent);
    color: var(--ui-on-accent);
    font-family: var(--ui-font);
    text-align: left;
  }

  .ui-new-game:focus-visible {
    outline: 3px solid var(--ui-ink);
    outline-offset: 3px;
  }

  .ui-new-game-plus {
    flex: 0 0 auto;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: color-mix(in srgb, var(--ui-on-accent) 20%, transparent);
  }

  .ui-new-game-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }

  .ui-new-game-text b {
    font: var(--ui-text-heading);
  }

  .ui-new-game-text span {
    font: var(--ui-text-label);
  }

  .ui-new-game-chevron {
    flex: 0 0 auto;
  }
</style>
